// Helpers tipados del MÓDULO 7 (Reportes gerenciales).
// Centraliza las llamadas al backend de reportes/dashboards por cada instancia
// (instrucción, juzgamiento, segunda instancia), los KPIs globales, el listado
// paginado de expedientes y la exportación (CSV/Excel) con descarga en el navegador.
// Base de la API y token JWT los resuelve `api`/`getToken` (lib/api.ts). Requiere Bearer.

import { api, getToken } from './api';

const API = process.env.NEXT_PUBLIC_API_URL ?? 'http://10.10.30.130:4601/api/v1';

// ---- RBAC -----------------------------------------------------------------

interface UsuarioSesion {
  id?: string;
  nombre?: string;
  rol?: string;
  [k: string]: unknown;
}

/** Lee el usuario en sesión desde localStorage ('usuario'). */
export function usuarioActual(): UsuarioSesion | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('usuario');
    return raw ? (JSON.parse(raw) as UsuarioSesion) : null;
  } catch {
    return null;
  }
}

// Roles que pueden ver el módulo de Reportes gerenciales.
export const ROLES_REPORTES_UI = [
  'ADMINISTRADOR',
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
];

/** ¿El rol dado puede ver los reportes gerenciales? */
export function puedeVerReportes(rol?: string): boolean {
  return !!rol && ROLES_REPORTES_UI.includes(rol);
}

// ---- Tipos del dominio (respuestas del backend) ---------------------------

export interface DashboardInstruccion {
  quejasTotales: number;
  sinReparto: number;
  indagacionesActivas: number;
  investigacionesActivas: number;
  enPruebas: number;
  proximosAPrescribir: number;
  vencidos: number;
  pliegos: number;
  archivos: number;
  remitidos: number;
  porEstado: Record<string, number>;
}

export interface DashboardJuzgamiento {
  recibidos: number;
  ordinarios: number;
  verbales: number;
  enAlegatos: number;
  variacion: number;
  fallos: number;
  recursos: number;
  aSegundaInstancia: number;
}

export interface DashboardSegunda {
  enSegundaInstancia: number;
  ejecutoriados: number;
  enEjecucion: number;
  cumplidas: number;
  archivoHistorico: number;
  falloSegundaPorSentido: Record<string, number>;
}

export interface Kpis {
  totalExpedientes: number;
  porcentajeArchivos: number;
  porcentajeConPliego: number;
  proximosAPrescribir: number;
  terminosVencidos: number;
}

export interface ExpedienteReporte {
  radicado: string;
  estado_actual: string;
  etapa_actual?: string | null;
  fecha_queja?: string | null;
  fecha_prescripcion?: string | null;
  funcionario_actual?: string | null;
  prioridad?: string | null;
}

export interface PaginaReporte {
  data: ExpedienteReporte[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface FiltrosExpedientes {
  estado?: string;
  funcionarioId?: string;
  anio?: string | number;
  page?: number;
  limit?: number;
}

// ---- Llamadas -------------------------------------------------------------

/** Dashboard de la instancia de instrucción. */
export function dashboardInstruccion(): Promise<DashboardInstruccion> {
  return api<DashboardInstruccion>('/reportes/dashboard/instruccion');
}

/** Dashboard de la instancia de juzgamiento. */
export function dashboardJuzgamiento(): Promise<DashboardJuzgamiento> {
  return api<DashboardJuzgamiento>('/reportes/dashboard/juzgamiento');
}

/** Dashboard de la segunda instancia. */
export function dashboardSegunda(): Promise<DashboardSegunda> {
  return api<DashboardSegunda>('/reportes/dashboard/segunda-instancia');
}

/** KPIs globales del sistema. */
export function kpis(): Promise<Kpis> {
  return api<Kpis>('/reportes/kpis');
}

/** Construye el query string de filtros (omite vacíos). */
function qsFiltros(f: FiltrosExpedientes): string {
  const qs = new URLSearchParams();
  if (f.estado) qs.set('estado', f.estado);
  if (f.funcionarioId) qs.set('funcionarioId', f.funcionarioId);
  if (f.anio !== undefined && f.anio !== '') qs.set('anio', String(f.anio));
  if (f.page) qs.set('page', String(f.page));
  if (f.limit) qs.set('limit', String(f.limit));
  return qs.toString();
}

/** Listado paginado de expedientes para el reporte, con filtros opcionales. */
export function listarExpedientes(filtros: FiltrosExpedientes = {}): Promise<PaginaReporte> {
  const s = qsFiltros(filtros);
  return api<PaginaReporte>(`/reportes/expedientes${s ? `?${s}` : ''}`);
}

/**
 * Exporta el reporte de expedientes en el formato dado (CSV o Excel).
 * Descarga autenticada del binario (Bearer) y disparo de la descarga en el
 * navegador mediante un objectURL + <a download>. Lanza Error {message} si falla.
 */
export async function exportarExpedientes(
  formato: 'csv' | 'excel',
  filtros: FiltrosExpedientes = {},
): Promise<void> {
  const token = getToken();
  // Solo los filtros (sin paginación) importan en la exportación.
  const s = qsFiltros({
    estado: filtros.estado,
    funcionarioId: filtros.funcionarioId,
    anio: filtros.anio,
  });
  const sep = s ? `&${s}` : '';
  const res = await fetch(`${API}/reportes/expedientes/export?formato=${formato}${sep}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const m = (data as { message?: unknown } | null)?.message;
    throw new Error(
      Array.isArray(m) ? m.join(' · ') : (typeof m === 'string' ? m : 'No se pudo exportar el reporte.'),
    );
  }
  const blob = await res.blob();
  const nombre = formato === 'excel' ? 'reporte-expedientes.xlsx' : 'reporte-expedientes.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Etiquetas de estados (para mostrar el porEstado legible) --------------

/** Convierte un enum en MAYÚSCULAS_CON_GUIONES a texto legible en español. */
export function etiquetaEstado(estado: string): string {
  const conocidas: Record<string, string> = {
    QUEJA_RECIBIDA: 'Notificación disciplinaria recibida',
    SIN_REPARTO: 'Sin reparto',
    INDAGACION_PREVIA: 'Indagación previa',
    INVESTIGACION_DISCIPLINARIA: 'Investigación disciplinaria',
    EN_PRUEBAS: 'En pruebas',
    PLIEGO_DE_CARGOS: 'Pliego de cargos',
    ARCHIVO: 'Archivo',
    REMITIDO: 'Remitido',
    EN_JUZGAMIENTO: 'En juzgamiento',
    EN_ALEGATOS: 'En alegatos',
    FALLO: 'Fallo',
    SEGUNDA_INSTANCIA: 'Segunda instancia',
    EJECUTORIADO: 'Ejecutoriado',
    EN_EJECUCION: 'En ejecución',
  };
  if (conocidas[estado]) return conocidas[estado];
  return estado
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}
